import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

const SystemPromptModal = ({ client, isOpen, onClose, onSave }) => {
  const [useDefaultPrompt, setUseDefaultPrompt] = useState(true);
  const [customPrompt, setCustomPrompt] = useState('');
  const [useDefaultNoContextMessage, setUseDefaultNoContextMessage] = useState(true);
  const [customNoContextMessage, setCustomNoContextMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client && isOpen) {
      setUseDefaultPrompt(client.chatbotConfig?.useDefaultPrompt ?? true);
      setCustomPrompt(client.chatbotConfig?.systemPrompt || '');
      setUseDefaultNoContextMessage(client.chatbotConfig?.useDefaultNoContextMessage ?? true);
      setCustomNoContextMessage(client.chatbotConfig?.noContextMessage || '');
    }
  }, [client, isOpen]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave({
        useDefaultPrompt,
        systemPrompt: useDefaultPrompt ? null : customPrompt,
        useDefaultNoContextMessage,
        noContextMessage: useDefaultNoContextMessage ? null : customNoContextMessage
      });
      onClose();
    } catch (error) {
      console.error('Error saving system prompt:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">System Prompt Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* Use Default Prompt Option */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="useDefaultPrompt"
                checked={useDefaultPrompt}
                onChange={(e) => setUseDefaultPrompt(e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <label htmlFor="useDefaultPrompt" className="font-medium text-gray-900">
                  Use Default System Prompt
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  When enabled, this client will use the default system prompt managed by admin. 
                  Uncheck to create a custom prompt for this client.
                </p>
              </div>
            </div>

            {/* Custom Prompt Section */}
            {!useDefaultPrompt && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Custom System Prompt
                </label>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                  <div className="flex items-start">
                    <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">Template Variable:</p>
                      <p>Use <code className="bg-blue-100 px-1 rounded">{'{contextText}'}</code> where you want the client's context information to be inserted.</p>
                    </div>
                  </div>
                </div>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter custom system prompt for this client..."
                  className="w-full h-40 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                />
                <p className="text-sm text-gray-500">
                  This prompt will guide how the AI assistant behaves for this specific client.
                </p>
              </div>
            )}

            {/* Preview Info */}
            {useDefaultPrompt && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <p className="text-sm text-gray-700">
                  <strong>Using Default Prompt:</strong> This client will automatically use the system-wide default prompt. 
                  Changes to the default prompt will apply to all clients using this setting.
                </p>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">No Context Message</h3>
              
              {/* Use Default No Context Message Option */}
              <div className="flex items-start space-x-3 mb-4">
                <input
                  type="checkbox"
                  id="useDefaultNoContextMessage"
                  checked={useDefaultNoContextMessage}
                  onChange={(e) => setUseDefaultNoContextMessage(e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <label htmlFor="useDefaultNoContextMessage" className="font-medium text-gray-900">
                    Use Default "No Context" Message
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    When enabled, this client will use the default message when no relevant context is found. 
                    Uncheck to create a custom message for this client.
                  </p>
                </div>
              </div>

              {/* Custom No Context Message */}
              {!useDefaultNoContextMessage && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Custom "No Context" Message
                  </label>
                  <textarea
                    value={customNoContextMessage}
                    onChange={(e) => setCustomNoContextMessage(e.target.value)}
                    placeholder="Enter the message to show when no relevant context is found..."
                    className="w-full h-24 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                  />
                  <p className="text-sm text-gray-500">
                    This message will be shown to users when their question doesn't match any of the client's context information.
                  </p>
                </div>
              )}

              {/* No Context Preview Info */}
              {useDefaultNoContextMessage && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <p className="text-sm text-gray-700">
                    <strong>Using Default Message:</strong> This client will automatically use the system-wide default "no context" message. 
                    Changes to the default message will apply to all clients using this setting.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || (!useDefaultPrompt && !customPrompt.trim()) || (!useDefaultNoContextMessage && !customNoContextMessage.trim())}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemPromptModal;